import React, { PropsWithChildren, useState } from "react";

import Tooltip from "./Tooltip";
import { Duplicate } from "./icons";
import { twMerge } from "tailwind-merge";

interface CopyToClipboardProps extends PropsWithChildren {
  hideCopyIcon?: boolean;
  className?: string;
  textToCopy?: string;
  alwaysShowCopyIcon?: boolean;
  iconClassName?: string;
}

const copyToClipboardFn = (value = "") => {
  void navigator.clipboard.writeText(value);
};

export function CopyToClipboard(props: CopyToClipboardProps) {
  const {
    hideCopyIcon,
    children,
    className,
    textToCopy,
    alwaysShowCopyIcon,
    iconClassName,
  } = props;

  const [copied, setCopied] = useState(false);
  const textToCopyFromChildren =
    textToCopy ?? getTextToCopyFromChildren(children);

  return (
    <div className={twMerge("group/copy flex items-center", className)}>
      <div className="flex overflow-hidden">{children}</div>
      {!hideCopyIcon && textToCopyFromChildren && (
        <div>
          <Tooltip
            position="bottom"
            className="normal-case"
            message={copied ? "Copied to Clipboard" : ""}
          >
            <div>
              <Duplicate
                className={twMerge(
                  "ml-1 hidden cursor-pointer text-[16px] text-chariot-text-primary group-hover/copy:block",
                  iconClassName,
                  alwaysShowCopyIcon && "block"
                )}
                onClick={(e) => {
                  e.stopPropagation();
                  copyToClipboardFn(textToCopyFromChildren);
                  setCopied(true);
                  setTimeout(() => {
                    setCopied(false);
                  }, 2000);
                }}
              />
            </div>
          </Tooltip>
        </div>
      )}
    </div>
  );
}

function getTextToCopyFromChildren(children: React.ReactNode): string {
  if (typeof children === "string") {
    return children.trim();
  }

  if (React.isValidElement(children)) {
    const props = children.props as Record<string, unknown>;

    if (typeof props.text === "string") {
      return props.text.trim();
    }

    if (
      props.children &&
      typeof props.children === "object" &&
      Array.isArray(props.children)
    ) {
      return getTextToCopyFromChildren(props.children[0] as React.ReactNode);
    }

    return getTextToCopyFromChildren(props.children as React.ReactNode);
  }

  return "";
}
